import { createAsyncThunk } from '@reduxjs/toolkit';
import { message } from 'antd';
import produce from 'immer';
import _get from 'lodash/get';
import _has from 'lodash/has';
import _merge from 'lodash/merge';
import _omit from 'lodash/omit';
import _toLower from 'lodash/toLower';
import { v4 as uuid } from 'uuid';

import {
  formatTierServerError,
  isTierServerError
} from 'containers/Campaign/Referral/TierLadder/errors';
import { getCampaignPreparedToBeSent } from 'containers/Customer/utils';
import { getOutcomeType } from 'containers/Inventory/components/LedgerList';
import { OUTCOME_TYPE } from 'pages/campaign/constant';
import i18n from 'utils/i18n';
import request from 'utils/request';

import { DURATION_TIMER, REQUEST_TYPE } from '../../appConstants';
import {
  CREATE_CAMPAIGN,
  CREATE_CAMPAIGN_SUCCESS,
  CREATE_CAMPAIGN_FAIL,
  GET_CAMPAIGNS,
  GET_CAMPAIGNS_SUCCESS,
  GET_CAMPAIGNS_FAIL,
  GET_TAGS,
  GET_TAGS_FAIL,
  GET_TAGS_SUCCESS,
  GET_LOYALTY_LIST,
  GET_LOYALTY_LIST_SUCCESS,
  GET_LOYALTY_LIST_FAIL,
  GET_REWARDS,
  GET_REWARDS_SUCCESS,
  GET_REWARDS_FAIL,
  GET_AUDIENCES,
  GET_AUDIENCES_FAIL,
  GET_AUDIENCES_SUCCESS,
  GET_AUDIENCE_INFO,
  GET_AUDIENCE_INFO_SUCCESS,
  GET_AUDIENCE_INFO_FAILED,
  EDIT_CAMPAIGN,
  EDIT_CAMPAIGN_FAIL,
  EDIT_CAMPAIGN_SUCCESS,
  SEARCH_CAMPAIGN,
  SEARCH_CAMPAIGN_SUCCESS,
  SEARCH_CAMPAIGN_FAIL,
  SEARCH_REWARD,
  SEARCH_REWARD_SUCCESS,
  SEARCH_REWARD_FAIL,
  ORDER_CAMPAIGN,
  ORDER_CAMPAIGN_FAIL,
  ORDER_CAMPAIGN_SUCCESS,
  DEACTIVATE_CAMPAIGN,
  DEACTIVATE_CAMPAIGN_SUCCESS,
  DEACTIVATE_CAMPAIGN_FAIL,
  ACTIVATE_CAMPAIGN,
  ACTIVATE_CAMPAIGN_SUCCESS,
  ACTIVATE_CAMPAIGN_FAIL,
  GET_CONDITIONS,
  GET_CONDITIONS_SUCCESS,
  GET_CONDITIONS_FAIL,
  GET_STREAK_SETTING_DATA_REQUEST,
  GET_STREAK_SETTING_DATA_SUCCESS,
  GET_STREAK_SETTING_DATA_FAILURE
} from './constants';
import { TCampaignCreatePage } from './i18n/TModules';

/** `onDone(response)` on success, `onDone(undefined, error)` on failure; a null `history` stays put. */
export const campaignCreate = (
  payload,
  history,
  makerCheckerCampaign = false,
  isImageFeatureEnabled = false,
  isMultipleRaffleTicketsEnabled = false,
  onDone?: (response?: unknown, error?: unknown) => void
) => {
  const data = getCampaignPreparedToBeSent(payload, true, isMultipleRaffleTicketsEnabled);
  //remove display properties from games and only use locale version
  const shouldRemoveDisplayProperties = isImageFeatureEnabled
    ? data.campaign_type === 'game' && data.game_type !== 'quiz' && data.game_type !== 'survey'
    : (data.campaign_type === 'game' && data.game_type !== 'quiz' && data.game_type !== 'survey') ||
      data.campaign_type === 'stamp' ||
      data.campaign_type === 'quest' ||
      data.campaign_type === 'raffle';

  if (shouldRemoveDisplayProperties) {
    const dataKeys = Object.keys(data);
    const displayKeys = dataKeys.filter(k => k.startsWith('display_properties_'));
    displayKeys.forEach(k => {
      if (!!data[k]) {
        if (Object.keys(data[k]).length > 0) {
          data[k] = _merge({}, data.display_properties, data[k]);
        }
      }
    });
    delete data.display_properties;
  }
  if (data.campaign_type === 'quest' || data.campaign_type === 'progress') {
    if ('campaign_duration_mechanics' in data) {
      if (!data.campaign_duration_mechanics && 'campaign_duration_days' in data) {
        delete data.campaign_duration_days;
      }
      delete data.campaign_duration_mechanics;
    }
  }

  if (data.campaign_type !== 'quest' && data.campaign_type !== 'progress') {
    delete data.notify_on_update;
  }

  return {
    type: CREATE_CAMPAIGN,
    endpoint: `/v4/dash/campaigns`,
    method: 'post',
    types: [CREATE_CAMPAIGN, CREATE_CAMPAIGN_SUCCESS, CREATE_CAMPAIGN_FAIL],
    notification: {
      loading: () => {
        message.loading(i18n.t(`${TCampaignCreatePage}.CreatingACampaign`), DURATION_TIMER);
      },
      success: response => {
        if (response?.meta?.campaign_request_id) {
          message.success(
            i18n.t(`${TCampaignCreatePage}.CampaignSubmittedForApproval`),
            DURATION_TIMER
          );
        } else {
          message.success(i18n.t(`${TCampaignCreatePage}.CampaignCreated`), DURATION_TIMER);
        }
      },
      error: e => {
        if (isTierServerError(e)) {
          message.error(formatTierServerError(e), DURATION_TIMER);
        } else if (e.message) {
          message.error(e.message, DURATION_TIMER);
        } else {
          message.error(i18n.t(`${TCampaignCreatePage}.FailedToCreateCampaign`), DURATION_TIMER);
        }
      }
    },
    callback: onDone,
    instantAction: response => {
      if (history) {
        history(`/p/campaigns/show/${response.data.id}`);
      }
    },
    formdata: data
  };
};

export const campaignDeactivate = (payload, callback?) => {
  return {
    type: DEACTIVATE_CAMPAIGN,
    endpoint: `/v4/dash/campaigns/${payload.id}/deactivate`,

    method: 'patch',
    types: [DEACTIVATE_CAMPAIGN, DEACTIVATE_CAMPAIGN_SUCCESS, DEACTIVATE_CAMPAIGN_FAIL],
    notification: {
      loading: () => {
        message.loading(i18n.t(`${TCampaignCreatePage}.DeactivatingCampaign`), DURATION_TIMER);
      },
      success: response => {
        if (response?.data?.request_type) {
          message.success(
            i18n.t(`${TCampaignCreatePage}.CampaignDeactivationRequestSubmittedForApproval`),
            DURATION_TIMER
          );
        } else {
          message.success(i18n.t(`${TCampaignCreatePage}.DeactivatingSuccess`), DURATION_TIMER);
        }
      },
      error: () => {
        message.error(i18n.t(`${TCampaignCreatePage}.FailedToDeactivate`), DURATION_TIMER);
      }
    },
    callback: callback,
    nextAction: payload.page === 'list' ? campaignsGet() : undefined
  };
};

export const campaignActivate = (payload, callback?) => {
  return {
    type: ACTIVATE_CAMPAIGN,
    endpoint: `/v4/dash/campaigns/${payload.id}/activate`,

    method: 'patch',
    types: [ACTIVATE_CAMPAIGN, ACTIVATE_CAMPAIGN_SUCCESS, ACTIVATE_CAMPAIGN_FAIL],
    notification: {
      loading: () => {
        message.loading(i18n.t(`${TCampaignCreatePage}.ActivatingCampaign`), DURATION_TIMER);
      },
      success: response => {
        if (response?.data?.request_type) {
          message.success(
            i18n.t(`${TCampaignCreatePage}.CampaignActivationRequestSubmittedForApproval`),
            DURATION_TIMER
          );
        } else {
          message.success(i18n.t(`${TCampaignCreatePage}.ActivatingSuccess`), DURATION_TIMER);
        }
      },
      error: () => {
        message.error(i18n.t(`${TCampaignCreatePage}.FailedToActivate`), DURATION_TIMER);
      }
    },
    callback,
    nextAction: payload.page === 'list' ? campaignsGet() : undefined,
    formdata: payload
  };
};

const getRedirectPath = (response, currentCampaignId) => {
  const responseData = response?.data;

  const state = {
    campaignRequestId: responseData?.id,
    campaignCreatorId: responseData?.creator_id,
    requestType: responseData?.request_type
  };

  if (responseData?.request_type && responseData?.request_type === REQUEST_TYPE.UPDATE) {
    return {
      pathname: `/p/campaigns/show/${currentCampaignId}/campaign_request/${responseData?.id}`,
      state
    };
  }
  return `/p/campaigns/show/${currentCampaignId}`;
};

/** `onDone(response)` on success, `onDone(undefined, error)` on failure; a null `history` stays put. */
export const campaignUpdate = (
  payload,
  history,
  onDone?: (response?: any, error?: any) => void
) => {
  const { requestConfig, campaignId } = payload;

  return {
    type: EDIT_CAMPAIGN,
    endpoint: requestConfig.endpoint,
    method: requestConfig.method,
    types: [EDIT_CAMPAIGN, EDIT_CAMPAIGN_SUCCESS, EDIT_CAMPAIGN_FAIL],
    notification: {
      loading: () => {
        message.loading(i18n.t(`${TCampaignCreatePage}.UpdatingCampaign`), DURATION_TIMER);
      },
      success: response => {
        if (response?.meta?.campaign_request_id) {
          message.success(
            i18n.t(`${TCampaignCreatePage}.CampaignUpdateRequestSubmittedForApproval`),
            DURATION_TIMER
          );
        } else {
          message.success(requestConfig.successMessage, DURATION_TIMER);
        }
      },
      error: e => {
        if (isTierServerError(e)) {
          message.error(formatTierServerError(e), DURATION_TIMER);
        } else if (e.message) {
          message.error(e.message, DURATION_TIMER);
        } else {
          message.error(i18n.t(`${TCampaignCreatePage}.FailedToUpdate`), DURATION_TIMER);
        }
      }
    },
    callback: onDone,
    instantAction: response => {
      if (!history) return;
      const redirectPath = getRedirectPath(response, campaignId);
      // RR6 navigate signature: state must be passed as the second arg, not inside the To object
      if (typeof redirectPath === 'object') {
        history(redirectPath.pathname, { state: redirectPath.state });
      } else {
        history(redirectPath);
      }
    },
    formdata: requestConfig.formdata
  };
};

export const getPopulatedOutcomes = async (outcomes = []) => {
  try {
    let preparedOutcomes = [];

    const sortedOutcomes = [...outcomes].sort((a, b) => a.id - b.id);

    const prizeSets = sortedOutcomes?.filter(_ => _.type === OUTCOME_TYPE.PRIZE_SET) || [];
    const randomPrizeSet =
      sortedOutcomes?.filter(_ => _.type === OUTCOME_TYPE.RANDOMIZED_OUTCOME_SET) || [];
    const prizeSetItems = prizeSets.map(prizeSet => prizeSet?.items);
    const randomizeOutcomeItems = randomPrizeSet.map(randomPrize => randomPrize?.outcomes);
    const rewardIdsSet = new Set();
    const loyaltyIdsSet = new Set();
    const badgeIdsSet = new Set();
    const combinedItems = [...prizeSetItems, ...randomizeOutcomeItems];
    for (const prizeSet of combinedItems) {
      for (const item of prizeSet) {
        if (
          [OUTCOME_TYPE.REWARD_CAMPAIGN, OUTCOME_TYPE.REWARD].includes(item.item_type || item.type)
        ) {
          rewardIdsSet.add(item.item_id || item.id);
        } else if ([OUTCOME_TYPE.BADGE].includes(item.item_type || item.type)) {
          badgeIdsSet.add(item.item_id || item.id);
        } else {
          loyaltyIdsSet.add(item.item_id || item.id);
        }
      }
    }
    const rewardIds = Array.from(rewardIdsSet);
    const loyaltyIds = Array.from(loyaltyIdsSet);
    const badgeIds = Array.from(badgeIdsSet);

    // Collect game try campaign IDs to fetch their names
    const gameTryCampaignIdsSet = new Set<number>();
    for (const outcome of sortedOutcomes) {
      if (outcome.type === OUTCOME_TYPE.GAME_TRY) {
        gameTryCampaignIdsSet.add(outcome.id);
      }
    }
    const gameTryCampaignIds = Array.from(gameTryCampaignIdsSet);

    const [{ data: rewards }, { data: loyalties }, { data: badges }, { data: gameTryCampaigns }] =
      await Promise.all([
        rewardIds.length > 0
          ? request(`/v4/dash/rewards?id=${rewardIds.join(',')}`)
          : Promise.resolve({ data: [] }),
        loyaltyIds.length > 0
          ? request(`/v4/dash/loyalty_programs?id=${loyaltyIds.join(',')}`)
          : Promise.resolve({ data: [] }),
        badgeIds.length > 0
          ? request(`/v4/dash/badges?id=${badgeIds.join(',')}`)
          : Promise.resolve({ data: [] }),
        gameTryCampaignIds.length > 0
          ? request(`/v4/dash/campaigns?id=${gameTryCampaignIds.join(',')}`)
          : Promise.resolve({ data: [] })
      ]);

    const prizeSetItemsById = prizeSets.reduce(
      (acc, prizeSet, index) => ({ ...acc, [prizeSet.id]: prizeSetItems[index] }),
      {}
    );

    const randomPrizeSetById = randomPrizeSet.reduce(
      (acc, randomPrizeSet, index) => ({
        ...acc,
        [randomPrizeSet.id]: randomizeOutcomeItems[index]
      }),
      {}
    );

    const rewardsById = rewards?.data?.reduce((acc, item) => ({ ...acc, [item.id]: item }), {});
    const loyaltiesById = loyalties?.data?.reduce((acc, item) => ({ ...acc, [item.id]: item }), {});
    const badgesById = badges?.data?.reduce((acc, item) => ({ ...acc, [item.id]: item }), {});
    const gameTryCampaignsById = (gameTryCampaigns?.data || gameTryCampaigns || []).reduce(
      (acc, item) => ({ ...acc, [item.id]: item }),
      {}
    );

    let prizeSetCount = 0;
    let randomPrizeSetCount = 0;
    for (const outcome of sortedOutcomes) {
      if (outcome.type === OUTCOME_TYPE.PRIZE_SET) {
        prizeSetCount++;
        const prizeSetOutcomes = prizeSetItemsById[outcome.id].map(prizeSetItem => {
          const prizeSetItemType = _has(prizeSetItem, 'item_type')
            ? _toLower(prizeSetItem.item_type)
            : '';
          if (
            [OUTCOME_TYPE.REWARD_CAMPAIGN, OUTCOME_TYPE.REWARD].includes(prizeSetItem.item_type)
          ) {
            return {
              ...rewardsById[prizeSetItem.item_id],
              ...prizeSetItem,
              type: OUTCOME_TYPE.REWARD,
              _id: uuid()
            };
          } else if (OUTCOME_TYPE.GAME_TRY === prizeSetItemType) {
            return {
              ...prizeSetItem,
              type: OUTCOME_TYPE.GAME_TRY,
              item_type: OUTCOME_TYPE.GAME_TRY,
              name: `Game Try Campaign ID: ${prizeSetItem.item_id}`,
              _id: uuid()
            };
          } else {
            return {
              ...loyaltiesById[prizeSetItem.item_id],
              ...prizeSetItem,
              type: OUTCOME_TYPE.POINT,
              _id: uuid()
            };
          }
        });

        preparedOutcomes.push({
          ...outcome,
          name: outcome.name || `Prize Set ${prizeSetCount}`,
          _id: uuid(),
          new_api: true,
          outcomes: prizeSetOutcomes
        });
      } else if (outcome.type === OUTCOME_TYPE.RANDOMIZED_OUTCOME_SET) {
        randomPrizeSetCount++;

        const prizeSetOutcomes = randomPrizeSetById[outcome.id].map(randomPrizeSetItem => {
          if (
            [OUTCOME_TYPE.REWARD_CAMPAIGN, OUTCOME_TYPE.REWARD].includes(
              randomPrizeSetItem.item_type
            )
          ) {
            return {
              ...rewardsById[randomPrizeSetItem.item_id],
              ...randomPrizeSetItem,
              type: OUTCOME_TYPE.REWARD,
              _id: uuid()
            };
          } else if ([OUTCOME_TYPE.BADGE].includes(randomPrizeSetItem.item_type)) {
            return {
              ...badgesById[randomPrizeSetItem.item_id],
              ...randomPrizeSetItem,
              type: OUTCOME_TYPE.BADGE,
              _id: uuid()
            };
          } else if (OUTCOME_TYPE.GAME_TRY === randomPrizeSetItem.item_type) {
            return {
              ...randomPrizeSetItem,
              type: OUTCOME_TYPE.GAME_TRY,
              item_type: OUTCOME_TYPE.GAME_TRY,
              name: `Game Try Campaign ID: ${randomPrizeSetItem.item_id}`,
              _id: uuid()
            };
          } else if ([OUTCOME_TYPE.CUSTOM].includes(randomPrizeSetItem.item_type)) {
            return {
              ...randomPrizeSetItem,
              type: OUTCOME_TYPE.CUSTOM,
              name: 'No outcome',
              identifier: 'no_outcome',
              _id: uuid()
            };
          } else if (OUTCOME_TYPE.GAME_TRY === randomPrizeSetItem.item_type) {
            return {
              ...randomPrizeSetItem,
              type: OUTCOME_TYPE.GAME_TRY,
              item_type: OUTCOME_TYPE.GAME_TRY,
              name: `Game Try Campaign ID: ${randomPrizeSetItem.item_id}`,
              _id: uuid()
            };
          } else {
            return {
              ...loyaltiesById[randomPrizeSetItem.item_id],
              ...randomPrizeSetItem,
              type: OUTCOME_TYPE.POINT,
              _id: uuid()
            };
          }
        });

        preparedOutcomes.push({
          ...outcome,
          name: `Random Prize Set ${randomPrizeSetCount}`,
          _id: uuid(),
          new_api: true,
          outcomes: prizeSetOutcomes
        });
      } else if (outcome.type === OUTCOME_TYPE.GAME_TRY) {
        const gameTryCampaign = gameTryCampaignsById[outcome.id];
        preparedOutcomes.push({
          ...outcome,
          type: OUTCOME_TYPE.GAME_TRY,
          name: gameTryCampaign?.name || `Game Try Campaign ID: ${outcome.id}`,
          referrer_outcome_value: outcome.referrer_outcome_value ?? outcome.total_reward_limit,
          outcome_value: outcome.outcome_value ?? outcome.total_referree_reward_limit,
          _id: uuid()
        });
      } else {
        preparedOutcomes.push({
          ...outcome,
          _id: uuid(),

          /**
           * To convert 'Reward::Campaign' and 'StoredValue::Campaign' to reward and points respectively
           * until backend won't accept the original types in PUT
           */
          type: outcome.type ? getOutcomeType(outcome.type).toLowerCase() : undefined
        });
      }
    }

    return preparedOutcomes.sort((a, b) => {
      if (a?.ordering != null && b?.ordering != null) return a.ordering - b.ordering;
      return 0;
    });
  } catch (error) {
    console.error(error);
    return [];
  }
};

/**
 * Enrich variable-points outcomes (count === null) with rule_details from the
 * campaign's transaction rules so the IssuePointsModal can prefill and the
 * display components can show the formula instead of "0 points".
 */
const enrichOutcomesWithRuleDetails = (outcomes: any[], rules: any[] = []) => {
  const variableRule = rules.find(
    r =>
      r.event_name === 'transactions_created' &&
      r.rule_type === 'campaign_rule' &&
      !r.send_notification
  );
  if (!variableRule) return outcomes;

  return outcomes.map(outcome => {
    if (outcome.type === 'points' && outcome.count == null && !outcome.rule_details) {
      return {
        ...outcome,
        rule_details: {
          id: variableRule.id,
          outcome_value: variableRule.outcome_value,
          outcome_base: variableRule.outcome_base,
          max_outcome_value: variableRule.max_outcome_value,
          conditions: variableRule.conditions || [],
          event_name: variableRule.event_name,
          outcome_type: variableRule.outcome_type,
          rule_type: variableRule.rule_type
        }
      };
    }
    return outcome;
  });
};

const toClawSlot = (value: any): number | null => {
  const slot = Number(value);
  return Number.isInteger(slot) && slot > 0 ? slot : null;
};

const toClawPrizeIcon = (icon: any, slot: number) =>
  icon?.value && typeof icon.value === 'object'
    ? { type: 'image', slot, value: { ...icon.value } }
    : null;

const getClawPrizeIconsBySlot = (data: any) => {
  // Every locale carries the same slot set, so the default locale is the deterministic source;
  // object key order is not, and picking a random locale would replicate it over the rest on save.
  const localeKeys = Object.keys(data)
    .filter(key => key.startsWith('display_properties_'))
    .sort((a, b) => Number(b === 'display_properties_en') - Number(a === 'display_properties_en'));
  const icons = [
    data.display_properties?.prize_icons,
    ...localeKeys.map(key => data[key]?.prize_icons)
  ].find(list => Array.isArray(list) && list.length > 0);

  const iconsBySlot = new Map<number, any>();
  (icons || []).forEach(icon => {
    const slot = toClawSlot(icon?.slot);
    const prizeIcon = slot === null ? null : toClawPrizeIcon(icon, slot);
    if (slot !== null && prizeIcon) {
      iconsBySlot.set(slot, prizeIcon);
    }
  });

  return iconsBySlot;
};

/**
 * Rebuild the claw slot/icon pair that the save path strips off each outcome. A prize setting is
 * keyed by `campaign_module_id` and the slot lives inside its stored `icon` (contract §6.5: no
 * column records a slot), so the icon node is the pairing source and the sibling `slot` key is only
 * read as a courtesy for payload shapes that hoist it.
 */
const attachClawPrizeSettings = (outcomes: any[], data: any) => {
  const prizeSettings = Array.isArray(data?.game_settings?.prize_settings)
    ? data.game_settings.prize_settings
    : [];
  const iconsBySlot = getClawPrizeIconsBySlot(data);

  if (prizeSettings.length === 0 && iconsBySlot.size === 0) return outcomes;

  const slotByModuleId = new Map<string, number>();
  const iconByModuleId = new Map<string, any>();

  prizeSettings.forEach(prizeSetting => {
    const slot = toClawSlot(prizeSetting?.slot) ?? toClawSlot(prizeSetting?.icon?.slot);
    if (slot === null || prizeSetting?.campaign_module_id == null) return;

    // The id round-trips as a number on the campaign and as a string on a maker/checker snapshot.
    const moduleId = String(prizeSetting.campaign_module_id);
    slotByModuleId.set(moduleId, slot);
    const icon = iconsBySlot.get(slot) || toClawPrizeIcon(prizeSetting?.icon, slot);
    if (icon) iconByModuleId.set(moduleId, icon);
  });

  return outcomes.map(outcome => {
    const moduleId =
      outcome?.campaign_module_id == null ? null : String(outcome.campaign_module_id);
    // A pending maker/checker snapshot carries no derived prize_settings, but
    // its outcomes still echo the slot the maker assigned.
    const slot = (moduleId ? slotByModuleId.get(moduleId) : undefined) ?? toClawSlot(outcome?.slot);
    if (slot == null) return outcome;

    const icon = (moduleId ? iconByModuleId.get(moduleId) : undefined) || iconsBySlot.get(slot);
    return icon ? { ...outcome, slot, icon } : { ...outcome, slot };
  });
};

export const getCampaignDetails = createAsyncThunk(
  'campaigns/getCampaignDetails',
  async (
    query:
      | string
      | number
      | { campaignId: string; campaign_request_id?: string; isDuplicate?: boolean }
  ) => {
    const campaignId = _get(query, 'campaignId', query);
    const campaign_request_id = _get(query, 'campaign_request_id', null);
    const isDuplicate = _get(query, 'isDuplicate', false);

    const endpoint = campaign_request_id
      ? `/v4/dash/campaign_requests/${campaign_request_id}/campaign`
      : `/v4/dash/campaigns/${campaignId}`;
    const response = await request.get(endpoint);

    const data = campaign_request_id
      ? {
          data: {
            ...response.data?.data?.campaign,
            changedProperties: new Set(response.data?.data?.changes?.changed_attributes || [])
          }
        }
      : response.data;

    const preparedData = produce(data, async draft => {
      const isBubblePop = draft.data.game_type === 'bubble_pop';
      const isClawMachine = draft.data.game_type === 'claw_machine';

      // Maker-checker round-trips arm_accuracy as a numeric string, and the
      // slider only honours a number.
      if (isClawMachine && typeof draft.data.game_settings?.arm_accuracy === 'string') {
        const rawArmAccuracy = draft.data.game_settings.arm_accuracy.trim();
        const armAccuracy = Number(rawArmAccuracy);
        if (rawArmAccuracy !== '' && Number.isFinite(armAccuracy)) {
          draft.data.game_settings.arm_accuracy = armAccuracy;
        }
      }

      if (isBubblePop) {
        const props = draft.data.properties || {};
        draft.data.game_settings = props;

        // The BubblePop component reads/writes display_properties (base).
        // On save, display_properties is merged into display_properties_<locale>
        // and deleted. So on GET, bubble fields live inside the locale key.
        // Extract them into base display_properties and remove from locale
        // keys so the save merge doesn't overwrite user edits.
        const bubbleFieldKeys = [
          'bubble_configuration',
          'bubble_spawn_rate',
          'spawn_rate_config',
          'game_duration',
          'target_type',
          'background_image',
          'always_visible'
        ];

        const localeKeys = Object.keys(draft.data).filter(
          k => k.startsWith('display_properties_') && draft.data[k]
        );

        const firstLocale = localeKeys.length > 0 ? draft.data[localeKeys[0]] : {};
        const bubbleFields: Record<string, any> = {};
        bubbleFieldKeys.forEach(key => {
          bubbleFields[key] = firstLocale[key] ?? props[key];
        });

        draft.data.display_properties = {
          ...draft.data.display_properties,
          ...bubbleFields
        };

        // Remove bubble fields from all locale keys so they don't
        // overwrite user edits during the save merge.
        localeKeys.forEach(k => {
          bubbleFieldKeys.forEach(field => {
            delete draft.data[k][field];
          });
        });
      }

      if (draft.data.outcomes) {
        let enrichedOutcomes = draft.data.outcomes;

        if (isBubblePop && draft.data.game_score_settings) {
          enrichedOutcomes = draft.data.outcomes.map(outcome => {
            const matchedScoreSetting = draft.data.game_score_settings.find(
              setting => setting.campaign_module_id === outcome.campaign_module_id
            );
            return {
              ...outcome,
              score: matchedScoreSetting?.score ?? null
            };
          });
        }

        if (isClawMachine) {
          enrichedOutcomes = attachClawPrizeSettings(enrichedOutcomes, draft.data);

          // Once harvested onto the outcomes, the icons must not stay in display_properties_<locale>:
          // the save merge keeps a destination key the new payload no longer carries, so a stale
          // fixed-mode array would survive a switch to random mode (same reason as bubble_pop above).
          Object.keys(draft.data)
            .filter(key => key.startsWith('display_properties_') && draft.data[key])
            .forEach(key => {
              delete draft.data[key].prize_icons;
            });
          if (draft.data.display_properties) {
            delete draft.data.display_properties.prize_icons;
          }
        }

        draft.data.outcomes = await getPopulatedOutcomes(enrichedOutcomes);
        draft.data.outcomes = enrichOutcomesWithRuleDetails(draft.data.outcomes, draft.data.rules);
      }
      if (
        draft.data.campaign_type === 'streak' &&
        draft.data.streak_campaign_mechanics.streak_mode === 'progressive' &&
        Array.isArray(draft.data.streak_intervals)
      ) {
        for (let interval of data.data.streak_intervals) {
          interval.outcomes = await getPopulatedOutcomes(
            interval.outcomes.map(outcome => {
              return {
                ...outcome,
                count: outcome?.points_count || outcome?.count,
                gl_account_id: outcome?.campaign_options?.outcome_properties?.gl_account_id,
                gl_account_name: outcome?.campaign_options?.outcome_properties?.gl_account_name,
                gl_account_type: outcome?.campaign_options?.outcome_properties?.gl_account_type,
                campaign_options: outcome?.campaign_options,
                expiry_unit: outcome?.expiry_unit,
                expiry_period: outcome?.expiry_period,
                expiry_fixed_date: outcome?.expiry_fixed_date,
                expiry_use_campaign_end_date: outcome?.expiry_use_campaign_end_date
              };
            })
          );
        }
      }

      if (draft.data.milestones) {
        for (let milestone of draft.data.milestones) {
          milestone.outcomes = await getPopulatedOutcomes(
            milestone.outcomes.map(outcome => {
              const base = outcome.outcome ? outcome.outcome : outcome;
              return {
                ...base,
                count: outcome?.points_count ?? outcome?.count ?? base?.count,
                gl_account_id: outcome?.campaign_options?.outcome_properties?.gl_account_id,
                gl_account_name: outcome?.campaign_options?.outcome_properties?.gl_account_name,
                gl_account_type: outcome?.campaign_options?.outcome_properties?.gl_account_type,
                campaign_options: outcome?.campaign_options,
                expiry_unit: outcome?.expiry_unit ?? base?.expiry_unit,
                expiry_period: outcome?.expiry_period ?? base?.expiry_period,
                expiry_fixed_date: outcome?.expiry_fixed_date ?? base?.expiry_fixed_date,
                expiry_use_campaign_end_date:
                  outcome?.expiry_use_campaign_end_date ?? base?.expiry_use_campaign_end_date
              };
            })
          );
        }
      }

      if (
        draft.data.campaign_type === 'streak' &&
        draft.data.streak_campaign_mechanics.streak_mode === 'continuous_loop' &&
        draft.data.streak_intervals[0]?.outcomes
      ) {
        draft.data.streak_intervals[0].outcomes = await getPopulatedOutcomes(
          draft.data.streak_intervals[0].outcomes
        );
      }

      // https://perxtechnologies.atlassian.net/browse/FP-180
      if (draft.data.raffle_grand_prizes) {
        for (let grandPrize of draft.data.raffle_grand_prizes) {
          grandPrize.outcomes = grandPrize.campaign_module_ids?.map(id => {
            const outcome = draft.data.outcomes?.find(outcome => outcome.campaign_module_id === id);
            return _omit(outcome, 'campaign_module_id');
          });

          // Delete id and campaign_module_ids because backend removes and recreates records
          delete grandPrize.id;
          delete grandPrize.campaign_module_ids;
        }
        delete draft.data.outcomes;
      }
      // https://perxtechnologies.atlassian.net/browse/FP-181
      // campaign_code_setting param has to be undefined when create/edit a raffle with user uploaded code.
      // But on GET requests it's returned as a non-empty object. Therefore, remove it from frontend data
      if (
        draft.data.campaign_code_setting?.code_generation_method === 'multiple_with_user_upload'
      ) {
        draft.data.campaign_code_setting = undefined;
      }

      if (isDuplicate && draft?.data?.campaign_code_setting?.id) {
        delete draft.data.campaign_code_setting.id;
      }
    });

    return preparedData;
  }
);

export const getRootCampaign = createAsyncThunk(
  'campaigns/getRootCampaign',
  async (query: string) => {
    // here query is campaign id
    const response = await request.get(`/v4/dash/campaigns/${query}`);

    const data = response.data;

    const preparedData = produce(data, async draft => {
      if (draft.data.outcomes) {
        draft.data.outcomes = await getPopulatedOutcomes(draft.data.outcomes);
        draft.data.outcomes = enrichOutcomesWithRuleDetails(draft.data.outcomes, draft.data.rules);
      }
      if (draft.data.milestones) {
        for (let milestone of draft.data.milestones) {
          milestone.outcomes = await getPopulatedOutcomes(
            milestone.outcomes.map(outcome => ({
              ...outcome.outcome,
              count: outcome?.points_count || outcome?.count,
              gl_account_id: outcome?.campaign_options?.outcome_properties?.gl_account_id,
              gl_account_name: outcome?.campaign_options?.outcome_properties?.gl_account_name,
              gl_account_type: outcome?.campaign_options?.outcome_properties?.gl_account_type,
              campaign_options: outcome?.campaign_options
            }))
          );
        }
      }
      // https://perxtechnologies.atlassian.net/browse/FP-180
      if (draft.data.raffle_grand_prizes) {
        for (let grandPrize of draft.data.raffle_grand_prizes) {
          grandPrize.outcomes = grandPrize.campaign_module_ids?.map(id => {
            const outcome = draft.data.outcomes?.find(outcome => outcome.campaign_module_id === id);
            return _omit(outcome, 'campaign_module_id');
          });

          // Delete id and campaign_module_ids because backend removes and recreates records
          delete grandPrize.id;
          delete grandPrize.campaign_module_ids;
        }
        delete draft.data.outcomes;
      }
      // https://perxtechnologies.atlassian.net/browse/FP-181
      // campaign_code_setting param has to be undefined when create/edit a raffle with user uploaded code.
      // But on GET requests it's returned as a non-empty object. Therefore, remove it from frontend data
      if (
        draft.data.campaign_code_setting?.code_generation_method === 'multiple_with_user_upload'
      ) {
        draft.data.campaign_code_setting = undefined;
      }
    });

    return preparedData;
  }
);

//TAGS for rewards fetch here
export const tagsGet = (payload?) => {
  return {
    type: GET_TAGS,
    endpoint: `/v4/dash/simple/tags?size=1000`,
    types: [GET_TAGS, GET_TAGS_SUCCESS, GET_TAGS_FAIL],
    notification: {
      error: () => {
        message.error(i18n.t(`${TCampaignCreatePage}.FailedToLoadData`), DURATION_TIMER);
      }
    },
    payload
  };
};

//CAMPAIGN ORDER
export const campaignOrder = payload => {
  return {
    type: ORDER_CAMPAIGN,
    endpoint: `/v4/dash/campaigns/ordering`,
    types: [ORDER_CAMPAIGN, ORDER_CAMPAIGN_SUCCESS, ORDER_CAMPAIGN_FAIL],
    method: 'patch',
    notification: {
      error: () => {
        message.error(i18n.t(`${TCampaignCreatePage}.FailedToOrderCampaign`), DURATION_TIMER);
      }
    },
    nextAction: campaignsGet({
      search_string: payload.search_string || '',
      state: payload.campaign_type
    }),
    params: payload
  };
};

//TAGS for rewards fetch here
export const campaignsGet = (payload?) => {
  return {
    type: GET_CAMPAIGNS,
    endpoint: `/v4/dash/campaigns/`,
    types: [GET_CAMPAIGNS, GET_CAMPAIGNS_SUCCESS, GET_CAMPAIGNS_FAIL],
    params: payload,
    notification: {
      error: () => {
        message.error(i18n.t(`${TCampaignCreatePage}.FailedToLoadData`), DURATION_TIMER);
      }
    }
  };
};

//TAGS for loyalty fetch here
export const loyaltylistGet = (payload?) => {
  return {
    type: GET_LOYALTY_LIST,
    endpoint: `/v4/dash/simple/loyalty`,
    types: [GET_LOYALTY_LIST, GET_LOYALTY_LIST_SUCCESS, GET_LOYALTY_LIST_FAIL],
    notification: {
      error: () => {
        message.error(i18n.t(`${TCampaignCreatePage}.FailedToLoadData`), DURATION_TIMER);
      }
    },
    params: payload
  };
};

export const rewardsGet = payload => {
  return {
    type: GET_REWARDS,
    endpoint: `/v4/dash/rewards`,
    types: [GET_REWARDS, GET_REWARDS_SUCCESS, GET_REWARDS_FAIL],
    notification: {
      error: () => {
        message.error(i18n.t(`${TCampaignCreatePage}.FailedToLoadData`), DURATION_TIMER);
      }
    },
    params: payload
  };
};

export const getStreakSettingData = () => {
  return {
    type: GET_STREAK_SETTING_DATA_REQUEST,
    endpoint: `/v4/dash/campaigns/streak/settings`,
    types: [
      GET_STREAK_SETTING_DATA_REQUEST,
      GET_STREAK_SETTING_DATA_SUCCESS,
      GET_STREAK_SETTING_DATA_FAILURE
    ],
    notification: {
      error: () => {
        message.error('Failed to Load Data!', DURATION_TIMER);
      }
    }
  };
};

//TAGS for rewards fetch here
export const audiencesGet = payload => {
  return {
    type: GET_AUDIENCES,
    endpoint: `/v4/dash/simple/audiences`,
    types: [GET_AUDIENCES, GET_AUDIENCES_SUCCESS, GET_AUDIENCES_FAIL],
    notification: {
      error: () => {
        message.error(i18n.t(`${TCampaignCreatePage}.FailedToLoadData`), DURATION_TIMER);
      }
    },
    params: payload
  };
};

//TAGS for rewards fetch here
export const audienceInfoGet = payload => {
  return {
    type: GET_AUDIENCE_INFO,
    endpoint: `/v4/dash/simple/audiences`,
    types: [GET_AUDIENCE_INFO, GET_AUDIENCE_INFO_SUCCESS, GET_AUDIENCE_INFO_FAILED],
    notification: {
      error: () => {
        message.error(i18n.t(`${TCampaignCreatePage}.FailedToLoadData`), DURATION_TIMER);
      }
    },
    params: payload
  };
};

export const campaignSearch = payload => {
  return {
    type: SEARCH_CAMPAIGN,
    endpoint: `/v4/dash/campaigns/`,
    types: [SEARCH_CAMPAIGN, SEARCH_CAMPAIGN_SUCCESS, SEARCH_CAMPAIGN_FAIL],
    notification: {
      error: () => {
        message.error(i18n.t(`${TCampaignCreatePage}.FailedToLoadData`), DURATION_TIMER);
      }
    },
    params: payload
  };
};

export const searchReward = payload => {
  return {
    type: SEARCH_REWARD,
    endpoint: `/v4/dash/rewards`,
    types: [SEARCH_REWARD, SEARCH_REWARD_SUCCESS, SEARCH_REWARD_FAIL],
    notification: {
      error: () => {
        message.error(i18n.t(`${TCampaignCreatePage}.FailedToLoadData`), DURATION_TIMER);
      }
    },
    params: payload
  };
};

export const getConditions = (payload?) => {
  return {
    type: GET_CONDITIONS,
    endpoint: `/v4/dash/simple/rule_templates`,
    types: [GET_CONDITIONS, GET_CONDITIONS_SUCCESS, GET_CONDITIONS_FAIL],
    notification: {
      error: () => {
        message.error(i18n.t(`${TCampaignCreatePage}.FailedToLoadData`), DURATION_TIMER);
      }
    },
    params: payload
  };
};
